import React from "react";
import { DateTime } from "luxon";
import { ReactRenderer } from "./lib/ReactRenderer";
import DateUtility from "./lib/dateUtility";
import { Chevron, FILM_PAGE, FilmPoster, comingText, durationText, fetchFilm, fetchFilms, filmUrl, showsText, whenText } from "./lib/film";
const Spinner = () => React.createElement("div", { className: "bi-film-loading" },
    React.createElement("i", { className: "fa-regular fa-spinner-third fa-spin" }));
const BackIcon = () => (React.createElement("svg", { width: "22", height: "22", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" },
    React.createElement("path", { d: "m15 6-6 6 6 6" })));
const PlayIcon = () => (React.createElement("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" },
    React.createElement("circle", { cx: "12", cy: "12", r: "9" }),
    React.createElement("path", { d: "m10 8.5 5 3.5-5 3.5z" })));
/**
 * The films, playing now or coming (boards Film and Film - bred skaerm): a toggle and a row per film
 * on a phone, a grid of posters on a wide screen.
 */
class FilmList extends React.Component {
    constructor(props) {
        super(props);
        this.state = { isLoaded: false, films: null, error: null, coming: false };
    }
    componentDidMount() {
        fetchFilms()
            .then((films) => this.setState({ isLoaded: true, films, error: null }))
            .catch((error) => this.setState({ isLoaded: true, films: null, error: error.message }));
    }
    list() {
        if (!this.state.isLoaded)
            return React.createElement(Spinner, null);
        if (this.state.error)
            return React.createElement("p", { className: "bi-sl-empty bi-sl-empty-card" }, this.state.error);
        const coming = this.state.coming;
        const films = coming ? this.state.films.comingSoon : this.state.films.nowShowing;
        if (films.length === 0) {
            return (React.createElement("p", { className: "bi-sl-empty bi-sl-empty-card" }, coming ? "Der er ingen film på vej lige nu." : "Der spiller ingen film lige nu."));
        }
        return (React.createElement("div", { className: "bi-film-list" }, films.map((film) => {
            const meta = [durationText(film.duration), showsText(film.shows)].filter((x) => x).join(" · ");
            const when = coming ? comingText(film) : `Næste: ${whenText(DateUtility.parse(film.nextShow)).replace(/^I /, "i ")}`;
            return (React.createElement("a", { key: film.id, className: "bi-film-item", href: filmUrl(film.id) },
                React.createElement(FilmPoster, { id: film.id }),
                React.createElement("span", { className: "bi-film-item-text" },
                    React.createElement("span", { className: "bi-film-item-title" }, film.title),
                    React.createElement("span", { className: "bi-film-item-meta" }, meta),
                    React.createElement("span", { className: "bi-film-item-next" }, when),
                    film.isLastWeek ? React.createElement("span", { className: "bi-chip bi-chip-role bi-film-item-tag" }, "Sidste uge") : null),
                React.createElement(Chevron, null)));
        })));
    }
    render() {
        const coming = this.state.coming;
        return (React.createElement("div", { className: "bi-film-page" },
            React.createElement("div", { className: "bi-film-head" },
                React.createElement("div", { className: "bi-film-head-text" },
                    React.createElement("h1", null, "Film"),
                    React.createElement("p", null, "Det, der spiller nu, og det, der kommer.")),
                React.createElement("div", { className: "bi-seg bi-film-seg", role: "group", "aria-label": "Vis film" },
                    React.createElement("button", { type: "button", "aria-pressed": !coming, onClick: () => this.setState({ coming: false }) }, "Spiller nu"),
                    React.createElement("button", { type: "button", "aria-pressed": coming, onClick: () => this.setState({ coming: true }) }, "Kommer snart"))),
            this.list()));
    }
}
const known = (value) => value !== null && value !== undefined;
/**
 * Ticket sales as the member site has always said them: `84 solgte, 12 reserverede, 46 ledige`.
 * A sales row synced before the free seats were stored has no reserved figure: `84 solgte, 58 ledige`.
 */
const Sales = ({ show }) => {
    if (!known(show.sold))
        return null;
    return (React.createElement("span", { className: "bi-film-show-sales" },
        React.createElement("strong", null, show.sold),
        " solgte",
        known(show.prebooked) ? `, ${show.prebooked} reserverede` : "",
        known(show.free) ? `, ${show.free} ledige` : ""));
};
/** A coming screening: a link to its shift when it has one a member can see. */
const ShowRow = ({ show }) => {
    const start = DateUtility.parse(show.startTime);
    const end = DateUtility.parse(show.endTime);
    const content = (React.createElement(React.Fragment, null,
        React.createElement("span", { className: "bi-film-show-when" },
            React.createElement("span", { className: "bi-film-show-day" }, DateUtility.rowDay(start)),
            React.createElement("span", { className: "bi-film-show-time" }, DateUtility.rowInterval(start, end)),
            show.specials ? React.createElement("span", { className: "bi-chip bi-film-show-special" }, show.specials) : null),
        React.createElement("span", { className: "bi-film-show-venue" }, show.venue),
        React.createElement(Sales, { show: show }),
        React.createElement("span", { className: "bi-film-show-tag" }, show.isMine ? React.createElement("span", { className: "bi-chip bi-chip-role" }, "Din vagt") : null),
        show.shiftId ? React.createElement(Chevron, null) : React.createElement("span", null)));
    return show.shiftId
        ? React.createElement("a", { className: "bi-film-show", href: `/vagtinfo/?shiftId=${show.shiftId}` }, content)
        : React.createElement("div", { className: "bi-film-show" }, content);
};
/** One film and its coming screenings (boards Film - detaljer and Film - detaljer, bred skaerm). */
class FilmPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = { isLoaded: false, film: null, error: null };
    }
    componentDidMount() {
        fetchFilm(this.props.id)
            .then((film) => this.setState({ isLoaded: true, film, error: null }))
            .catch((error) => this.setState({
            isLoaded: true,
            film: null,
            error: error.status === 404 ? "Filmen findes ikke, eller den er ikke længere på programmet." : error.message
        }));
    }
    /** "Spiller til" for a film that has opened, its premiere for one that has not. */
    runText(film) {
        const first = film.firstShow ? DateUtility.parse(film.firstShow) : null;
        const last = film.lastShow ? DateUtility.parse(film.lastShow) : null;
        if (first && first > DateTime.local())
            return `Premiere ${DateUtility.rowDay(first)}`;
        if (last)
            return `Spiller til ${DateUtility.rowDay(last)}`;
        return null;
    }
    body() {
        if (!this.state.isLoaded)
            return React.createElement(Spinner, null);
        if (this.state.error)
            return React.createElement("p", { className: "bi-sl-empty bi-sl-empty-card" }, this.state.error);
        const film = this.state.film;
        const run = this.runText(film);
        const duration = durationText(film.duration);
        const linked = film.shows.some((s) => s.shiftId);
        return (React.createElement("div", { className: "bi-film-detail" },
            React.createElement(FilmPoster, { id: film.id, className: "bi-film-detail-poster" }),
            React.createElement("div", { className: "bi-film-detail-main" },
                React.createElement("div", { className: "bi-film-detail-head" },
                    React.createElement("h1", null, film.title),
                    duration ? React.createElement("span", null, duration) : null,
                    run ? React.createElement("span", null, run) : null,
                    film.trailerUri
                        ? React.createElement("a", { className: "bi-btn bi-btn-outline bi-film-trailer", href: film.trailerUri, target: "_blank", rel: "noopener noreferrer" },
                            React.createElement(PlayIcon, null),
                            "Se trailer")
                        : null),
                React.createElement("section", { className: "bi-film-shows" },
                    React.createElement("h2", null, "Forestillinger"),
                    film.shows.length === 0
                        ? React.createElement("p", { className: "bi-sl-empty bi-sl-empty-card" }, "Der er ingen kommende forestillinger.")
                        : (React.createElement("div", { className: "bi-film-shows-card" },
                            React.createElement("div", { className: "bi-film-shows-columns", "aria-hidden": "true" },
                                React.createElement("span", null, "Forestilling"),
                                React.createElement("span", null, "Sal"),
                                React.createElement("span", null, "Billetter"),
                                React.createElement("span", null, "Vagt"),
                                React.createElement("span", null)),
                            film.shows.map((show) => React.createElement(ShowRow, { key: show.id, show: show })))),
                    linked ? React.createElement("p", { className: "bi-sl-section-note" }, "Tryk p\u00E5 en forestilling for at se vagten.") : null))));
    }
    render() {
        return (React.createElement("div", { className: "bi-film-page" },
            React.createElement("a", { className: "bi-film-back", href: FILM_PAGE },
                React.createElement(BackIcon, null),
                "Film"),
            this.body()));
    }
}
/** The Film page: the list of films, or one film when the address names it (`?film=<id>`). */
export default class FilmComponent extends React.Component {
    render() {
        const id = Number(new URLSearchParams(window.location.search).get("film"));
        return id > 0 ? React.createElement(FilmPage, { id: id }) : React.createElement(FilmList, null);
    }
}
ReactRenderer.render("filmcomponent", (elem) => React.createElement(FilmComponent, Object.assign({}, elem.dataset)));
